'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { ConversationWithDetails } from '../types';
import { useCurrentUser } from './use-current-user';

/**
 * Raw conversation data from database query
 */
type ConversationQueryRow = Omit<ConversationWithDetails, 'my_participation'>;

interface UseConversationsOptions {
  realtime?: boolean;
}

/**
 * Hook to fetch and subscribe to user conversations
 */
export function useConversations(options: UseConversationsOptions = {}) {
  const { realtime = true } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const { userId } = useCurrentUser();

  const queryKey = useMemo(() => ['chat-conversations'], []);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ConversationWithDetails[]> => {
      // Use cached userId instead of calling getUser()
      if (!userId) {
        return [];
      }

      const { data, error } = await client
        .from('conversations')
        .select(
          `
          *,
          participants:conversation_participants(
            *,
            profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url, phone_number)
          ),
          latest_message:messages(metadata)
        `,
        )
        .is('deleted_at', null)
        // Embed shape: project ONLY the latest message's metadata so
        // ConversationList can branch on `metadata.intent_type` without
        // a second round-trip. The order+limit apply to the embedded
        // `messages` rows (not the parent conversations rows).
        .order('created_at', {
          ascending: false,
          referencedTable: 'messages',
        })
        .limit(1, { referencedTable: 'messages' })
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) {
        throw error;
      }

      // Transform to ConversationWithDetails
      return (data || []).map((conv) => {
        const row = conv as unknown as ConversationQueryRow;
        const participants = row.participants || [];
        return {
          ...row,
          participants,
          my_participation:
            participants.find((p) => p.user_id === userId) || null,
        };
      });
    },
    enabled: !!userId, // Only run when user is loaded
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  // Set up realtime subscription for conversation updates
  useEffect(() => {
    if (!realtime || !userId) {
      return;
    }

    // Clean up existing subscription
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
    }

    // Subscribe to conversation updates
    const channel = client
      .channel('chat-conversations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [client, realtime, queryClient, queryKey, userId]);

  return query;
}

/**
 * Hook to get a single conversation by ID
 */
export function useConversation(conversationId: string | null) {
  const client = useSupabase();
  const { userId } = useCurrentUser();

  return useQuery({
    queryKey: ['chat-conversation', conversationId],
    queryFn: async (): Promise<ConversationWithDetails | null> => {
      if (!conversationId || !userId) return null;

      const { data, error } = await client
        .from('conversations')
        .select(
          `
          *,
          participants:conversation_participants(
            *,
            profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url, phone_number)
          )
        `,
        )
        .eq('id', conversationId)
        .is('deleted_at', null)
        .single();

      if (error) {
        throw error;
      }

      const row = data as unknown as ConversationQueryRow;
      const participants = row.participants || [];
      return {
        ...row,
        participants,
        my_participation:
          participants.find((p) => p.user_id === userId) || null,
      };
    },
    enabled: !!conversationId && !!userId,
    staleTime: 30000,
  });
}

/**
 * Hook to get total unread message count across all conversations
 */
export function useTotalUnreadCount() {
  const client = useSupabase();
  const queryClient = useQueryClient();
  const { userId } = useCurrentUser();

  const queryKey = useMemo(() => ['chat-total-unread-count'], []);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;

      const { data, error } = await client
        .from('conversation_participants')
        .select('unread_count')
        .eq('user_id', userId)
        .eq('is_active', true)
        .gt('unread_count', 0);

      if (error) {
        throw error;
      }

      return (data || []).reduce((sum, p) => sum + (p.unread_count || 0), 0);
    },
    enabled: !!userId,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Invalidation helper
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return { ...query, invalidate };
}
